/**
 * Inline banner for the channel-detail / video-detail pages —
 * surfaces any open corrective action for the (profile, channel|video)
 * pair with Apply / Dismiss / Revert affordances. Sits above the
 * scored content so the parent sees the suggestion in context.
 */
import React from 'react';
import {ApolloClient, NormalizedCacheObject} from '@apollo/client';
import {Box, Button, Chip, Stack, Typography} from '@mui/material';
import {
  useApplyCorrectiveAction,
  useDismissCorrectiveAction,
  useGetCorrectiveActionsFor,
  useRetractCorrectiveAction,
} from '../../api/graphql_corrective_actions';

interface Props {
  client: ApolloClient<NormalizedCacheObject>;
  profileId: string;
  channelId?: string;
  videoId?: string;
}

const POLARITY_CHROME: Record<string, {bg: string; fg: string; border: string; label: string}> = {
  block: {bg: '#FEF2F2', fg: '#991B1B', border: '#FCA5A5', label: 'Block'},
  follow: {bg: '#ECFDF5', fg: '#047857', border: '#A7F3D0', label: 'Follow'},
  undo: {bg: '#F1F5F9', fg: '#475569', border: '#CBD5E1', label: 'Undo'},
};

function polarityFor(type: string): keyof typeof POLARITY_CHROME {
  if (type.startsWith('FOLLOW')) return 'follow';
  if (type.startsWith('UNBLOCK') || type.startsWith('UNFOLLOW')) return 'undo';
  return 'block';
}

export default function PendingActionBanner({
  client,
  profileId,
  channelId,
  videoId,
}: Props): React.JSX.Element | null {
  const {data, refetch} = useGetCorrectiveActionsFor(client, profileId, {
    channelId,
    videoId,
    statuses: ['RECOMMENDED', 'TAKEN', 'EXECUTED'],
  });
  const [apply, {loading: applying}] = useApplyCorrectiveAction(client);
  const [dismiss, {loading: dismissing}] = useDismissCorrectiveAction(client);
  const [retract, {loading: retracting}] = useRetractCorrectiveAction(client);

  const rows = data?.getCorrectiveActionsFor ?? [];
  const open = rows.find((r: any) => r.status === 'RECOMMENDED');
  const taken = rows.find((r: any) => r.status === 'TAKEN' || r.status === 'EXECUTED');
  const row = open ?? taken;

  if (!row) return null;

  const chrome = POLARITY_CHROME[polarityFor(row.type)];
  const busy = applying || dismissing || retracting;

  const onApply = async () => {
    await apply({variables: {input: {actionId: row.id}}});
    await refetch();
  };
  const onDismiss = async () => {
    await dismiss({variables: {actionId: row.id}});
    await refetch();
  };
  const onRevert = async () => {
    await retract({variables: {actionId: row.id}});
    await refetch();
  };

  return (
    <Box
      sx={{
        border: `1px solid ${chrome.border}`,
        bgcolor: chrome.bg,
        borderRadius: 2,
        p: 1.75,
        mb: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <Chip
          label={chrome.label}
          size="small"
          sx={{bgcolor: chrome.fg, color: '#FFFFFF', fontWeight: 700, fontSize: 11, height: 22}}
        />
        {row.severity && (
          <Chip
            label={String(row.severity).toLowerCase()}
            size="small"
            sx={{bgcolor: '#FFFFFF', color: chrome.fg, fontSize: 11, height: 22}}
          />
        )}
        {row.tokenStatus === 'AWAITING' && (
          <Chip
            label="Waiting on YouTube"
            size="small"
            sx={{bgcolor: '#FFFBEB', color: '#92400E', fontSize: 11, height: 22}}
          />
        )}
        {row.status !== 'RECOMMENDED' && (
          <Chip
            label={String(row.status).toLowerCase()}
            size="small"
            sx={{bgcolor: '#FFFFFF', color: '#475569', fontSize: 11, height: 22}}
          />
        )}
      </Stack>
      <Typography sx={{fontSize: 14, fontWeight: 700, color: chrome.fg}}>{row.title}</Typography>
      <Typography sx={{fontSize: 13, color: chrome.fg, opacity: 0.85}}>{row.description}</Typography>
      <Stack direction="row" spacing={1}>
        {row.status === 'RECOMMENDED' && (
          <>
            <Button
              size="small"
              variant="contained"
              onClick={onApply}
              disabled={busy}
              sx={{textTransform: 'none', bgcolor: chrome.fg, '&:hover': {bgcolor: chrome.fg, opacity: 0.9}}}
            >
              {row.tokenStatus === 'AWAITING' ? 'Apply when ready' : 'Apply'}
            </Button>
            <Button size="small" variant="text" onClick={onDismiss} disabled={busy} sx={{textTransform: 'none', color: chrome.fg}}>
              Dismiss
            </Button>
          </>
        )}
        {(row.status === 'TAKEN' || row.status === 'EXECUTED') && (
          <Button size="small" variant="outlined" onClick={onRevert} disabled={busy} sx={{textTransform: 'none', color: chrome.fg, borderColor: chrome.border}}>
            Revert
          </Button>
        )}
      </Stack>
    </Box>
  );
}
